<template>
  <div id="scrollable-agent-configuration" class="agent-configuration">
    <!-- Sticky zero-height anchor: keeps the find bar pinned to the top of
         the screen while `#scrollable-agent-configuration` (this component's
         root) scrolls. -->
    <div v-if="isFindBarOpen" class="find-bar-anchor">
      <FindBar
        ref="findBarEl"
        container-id="scrollable-agent-configuration"
        @close="closeFindBar"
      />
    </div>
    <BaseTopBar v-if="isMounted" with-find @find="openFindBar">
      <template #left>
        <Breadcrumb :items="breadcrumbItems" />
      </template>
    </BaseTopBar>

    <div v-if="isLoading" class="agent-loading">
      <p>Loading agent...</p>
    </div>
    <template v-else-if="agent">
      <div class="configuration-content">
        <h1 class="configuration-title">{{ agent.name ?? "Agent" }}</h1>

        <BaseAlert v-if="hasEdited" state="warning" size="small">
          Changes will only apply to new conversations.
        </BaseAlert>

        <div class="configuration-section">
          <h3 class="configuration-label">User Preferences</h3>
          <p class="configuration-description">
            Enrich your assistant's and agents' context by linking (through
            <code>@</code>) information from your workspace: pages, projects,
            milestones, customers, and more.
          </p>
          <p class="configuration-description">
            Also connect integrations to give them access to tools like Linear,
            Notion, or Jira.
          </p>
          <div
            v-if="workspaceId"
            class="configuration-editor preferences-editor"
          >
            <div v-if="isUserPreferencesEmpty" class="editor-placeholder">
              Use <code>/</code> for commands and <code>@</code> to link objects
              and integrations.
            </div>
            <Editor
              ref="preferencesEditor"
              :notes="userPreferencesDocument"
              :workspace-id="workspaceId"
              :integration-organization-ids="integrationOrganizationIds"
              :integration-keys="aiIntegrationKeys"
              :org-id="referenceSearchOrganizationId"
              rect-container-selector=".agent-configuration"
              :editable="canWrite"
              @change="onUserPreferencesChange"
            />
          </div>
          <IntegrationPillsBar
            v-if="workspaceId"
            class="preferences-integration-pills"
            :workspace-id="workspaceId"
            :organization-ids="integrationOrganizationIds"
            @object-click="onObjectPillClick"
            @integration-click="onIntegrationPillClick"
          />
        </div>

        <div class="configuration-section">
          <h3 class="configuration-label">Agent Instructions</h3>
          <p class="configuration-description">
            Tell the agent what its purpose is and how it should respond.
          </p>
          <div v-if="workspaceId" class="configuration-editor">
            <div v-if="isAgentInstructionsEmpty" class="editor-placeholder">
              Use <code>/</code> for commands and <code>@</code> to link objects
              and integrations.
            </div>
            <Editor
              :notes="agentInstructionsDocument"
              :workspace-id="workspaceId"
              :integration-organization-ids="integrationOrganizationIds"
              :integration-keys="aiIntegrationKeys"
              :org-id="referenceSearchOrganizationId"
              rect-container-selector=".agent-configuration"
              :editable="canWrite"
              @change="onAgentInstructionsChange"
            />
          </div>
        </div>
      </div>
    </template>
    <div v-else class="agent-not-found">
      <p>Agent not found or not enabled.</p>
    </div>
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  onMounted,
  ref,
  shallowRef,
  toRefs,
} from "vue";
import { onBeforeRouteLeave } from "vue-router";

import type { OrgId, WorkspaceId } from "@ctrl/controller-rpc";

import { getAgentIntegrationOrganizationIds } from "@/components/ai/integrationOrganizations";
import IntegrationPillsBar, {
  integrationConnectionId,
  type IntegrationPill,
} from "@/components/ai/IntegrationPillsBar.vue";
import { isPromptDocumentEmpty } from "@/components/ai/stepPrompt";
import { useAgent } from "@/components/ai/useAgent";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb.vue";
import type { EditorInsertHandle } from "@/components/editor/Editor.vue";
import { isDefaultEmptyDoc } from "@/components/editor/utils";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import * as inject from "@/components/inject";
import { AI_INTEGRATION_KEYS } from "@/components/integrations/aiIntegrationKeys";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";

const Editor = defineAsyncComponent(
  () => import("@/components/editor/Editor.vue")
);

export default defineComponent({
  name: "AgentConfiguration",
  components: {
    BaseAlert,
    BaseTopBar,
    Breadcrumb,
    Editor,
    FindBar,
    IntegrationPillsBar,
  },
  props: {
    organizationId: { type: String, required: true },
    agentId: { type: String, required: true },
  },
  async setup(props) {
    const { organizationId, agentId } = toRefs(props);

    const controller = inject.controller();
    const profile = inject.profile();
    const logger = inject.logger("AgentConfiguration");
    const baseLogger = logger.base();

    const isMounted = ref(false);
    const hasEdited = ref(false);
    const releaser = useReleaser();

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    // Flush the editors' debounced edits when leaving the page, so the
    // controller has the latest content before the next mount re-fetches the
    // agent. The guard must be registered before the first `await` (it
    // attaches to the active component instance), so the flush is late-bound
    // once the agent loads below.
    let flushAgentEdits: (() => void) | undefined;
    onBeforeRouteLeave(() => flushAgentEdits?.());

    // Pill clicks append into the User Preferences editor.
    const preferencesEditor = ref<EditorInsertHandle | null>(null);
    const onObjectPillClick = () => {
      preferencesEditor.value?.insertTextAtEnd?.("[[");
    };
    const onIntegrationPillClick = (pill: IntegrationPill) => {
      preferencesEditor.value?.insertIntegrationAtEnd?.({
        integrationKey: pill.integration.integrationKey,
        connectionId: integrationConnectionId(pill.integration),
        name: pill.name,
      });
    };

    onMounted(() => {
      isMounted.value = true;
    });

    const missingValues =
      !organizationId.value || !agentId.value || !profile.value;
    if (missingValues) {
      logger.debug("AgentConfiguration", "Missing required values");
      return {
        workspaceId: ref(""),
        canWrite: ref(false),
        isMounted: ref(false),
        hasEdited,
        isFindBarOpen,
        findBarEl,
        openFindBar,
        closeFindBar,
        agent: ref(undefined),
        agentInstructionsDocument: ref({ blocks: [] }),
        userPreferencesDocument: ref({ blocks: [] }),
        workingMemoryDocument: ref({ blocks: [] }),
        integrationOrganizationIds: ref<OrgId[] | undefined>(undefined),
        referenceSearchOrganizationId: ref<OrgId | null | undefined>(undefined),
        aiIntegrationKeys: AI_INTEGRATION_KEYS,
        isLoading: ref(true),
        isUserPreferencesEmpty: ref(false),
        isAgentInstructionsEmpty: ref(false),
        onAgentInstructionsChange: () => {},
        onUserPreferencesChange: () => {},
        onWorkingMemoryChange: () => {},
        preferencesEditor,
        onObjectPillClick,
        onIntegrationPillClick,
        breadcrumbItems: computed<BreadcrumbItem[]>(() => []),
      };
    }

    const result = await releaser.add(
      useAgent(
        controller,
        profile,
        baseLogger,
        organizationId,
        agentId,
        undefined,
        "AgentConfiguration"
      )
    );
    flushAgentEdits = result.flushPendingEdits;

    const {
      agent,
      isLoading,
      agentInstructionsDocument,
      userPreferencesDocument,
      workingMemoryDocument,
      handleAgentInstructionsChange,
      handleUserPreferencesChange,
      handleWorkingMemoryChange,
    } = result;
    const integrationOrganizationIds = computed<OrgId[] | undefined>(() =>
      getAgentIntegrationOrganizationIds(
        profile.value,
        agent.value?.key === "assistant"
      )
    );
    // The Assistant acts on the user's behalf across every organization, so
    // its configuration editors may reference anything; other agents stay
    // scoped to their own organization (the editor's default).
    const referenceSearchOrganizationId = computed<OrgId | null | undefined>(
      () => (agent.value?.key === "assistant" ? null : undefined)
    );

    const workspaceId = await releaser.add(
      bindAsync(organizationId, async (orgId) => {
        if (!orgId) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );
    // A `reader` member of the organization's default workspace cannot edit
    // the agent's configuration documents.
    const canWrite = computed(() =>
      isWorkspaceWritable(profile.value, workspaceId.value)
    );

    const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
      const items: BreadcrumbItem[] = [];
      items.push({
        title: "Agents",
        link: {
          name: RouteNames.SettingsAgents,
          params: { orgId: organizationId.value },
        },
      });
      if (agent.value) {
        items.push({
          title: agent.value.name ?? "Agent",
          link: { name: RouteNames.Agent, params: { agentId: agentId.value } },
        });
      }
      items.push({
        title: "Configuration",
        link: { params: {} },
      });
      return items;
    });

    // Placeholder visibility: before the first edit, judge the persisted
    // document; afterwards, the live ProseMirror doc (which also sees inline
    // atoms like integration chips that carry no text).
    const latestInstructionsDoc = shallowRef<ProseMirrorNode | null>(null);
    const latestPreferencesDoc = shallowRef<ProseMirrorNode | null>(null);

    const isAgentInstructionsEmpty = computed(() =>
      latestInstructionsDoc.value !== null
        ? isDefaultEmptyDoc(latestInstructionsDoc.value)
        : isPromptDocumentEmpty(agentInstructionsDocument.value)
    );
    const isUserPreferencesEmpty = computed(() =>
      latestPreferencesDoc.value !== null
        ? isDefaultEmptyDoc(latestPreferencesDoc.value)
        : isPromptDocumentEmpty(userPreferencesDocument.value)
    );

    const onAgentInstructionsChange = (
      proseMirrorDoc: ProseMirrorNode,
      shouldFlush?: boolean
    ) => {
      latestInstructionsDoc.value = proseMirrorDoc;
      handleAgentInstructionsChange(proseMirrorDoc, shouldFlush);
    };

    const onUserPreferencesChange = (
      proseMirrorDoc: ProseMirrorNode,
      shouldFlush?: boolean
    ) => {
      latestPreferencesDoc.value = proseMirrorDoc;
      handleUserPreferencesChange(proseMirrorDoc, shouldFlush);
    };

    const onWorkingMemoryChange = (
      proseMirrorDoc: ProseMirrorNode,
      shouldFlush?: boolean
    ) => handleWorkingMemoryChange(proseMirrorDoc, shouldFlush);

    return {
      agent,
      workspaceId,
      canWrite,
      isMounted,
      isLoading,
      hasEdited,
      isFindBarOpen,
      findBarEl,
      openFindBar,
      closeFindBar,
      agentInstructionsDocument,
      userPreferencesDocument,
      workingMemoryDocument,
      integrationOrganizationIds,
      referenceSearchOrganizationId,
      aiIntegrationKeys: AI_INTEGRATION_KEYS,
      isUserPreferencesEmpty,
      isAgentInstructionsEmpty,
      onAgentInstructionsChange,
      onUserPreferencesChange,
      onWorkingMemoryChange,
      preferencesEditor,
      onObjectPillClick,
      onIntegrationPillClick,
      breadcrumbItems,
    };
  },
});
</script>

<style scoped lang="scss">
.agent-configuration {
  width: 100%;
  height: 100%;
  overflow-y: auto;

  .find-bar-anchor {
    position: sticky;
    top: 0;
    height: 0;
    z-index: $z-index-dropdown;
  }

  .configuration-content {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    width: 100%;
    max-width: 600px;
    margin: 64px auto;
    padding: 0;
    padding-top: 24px;
    box-sizing: border-box;

    .configuration-title {
      margin: 0;
      margin-bottom: 8px;
      @extend %font-h2;

      @include themed() {
        color: t($primary);
      }
    }

    .configuration-section {
      display: flex;
      flex-direction: column;
      margin-bottom: 24px;
    }

    .configuration-label {
      @extend %font-h4;
      margin: 0 0 12px 0;
      padding: 0 4px;
      @include themed() {
        color: t($primary);
      }
    }

    :deep(.alert) {
      width: auto;
    }

    .configuration-description {
      @extend %font-caption-regular;
      margin: 0 0 12px 0;
      padding: 0 4px;
      @include themed() {
        color: t($grey-50);
      }

      code {
        @extend %inline-code-chip;
      }
    }

    .configuration-editor {
      // Anchors the placeholder overlay; also paints the preferences box
      // above the pills bar tucked underneath (negative top margin), like
      // the chat input does, so the box's bottom edge stays visible.
      position: relative;
      padding: 12px 12px 24px 12px;
      border-radius: $border-radius-l;
      @include themed() {
        background-color: t($bg-alt);
        border: 1px solid t($border);
      }

      :deep(.drag-handle),
      :deep(.add-handle) {
        display: none;
      }

      .editor-placeholder {
        position: absolute;
        // The box's own padding, plus the editor's internal content padding
        // (same offsets as the step prompt placeholder).
        top: 12px;
        inset-inline: 12px;
        padding: 8px 10px;
        pointer-events: none;
        @extend %font-body-regular;
        @include themed() {
          color: t($grey-40);
        }

        code {
          @extend %inline-code-chip;
        }
      }
    }

    .preferences-editor {
      min-height: 185px;
    }

    // The bar's built-in 1px inline margins fit inside the chat input's
    // border; here it should line up with the editor box's outer edges.
    .preferences-integration-pills {
      margin-inline: 0;
    }
  }
}

.agent-not-found,
.agent-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;

  p {
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
